import dayjs, { Dayjs } from "dayjs";
import { ErrorsEnum } from "@/src/modals/b2b/shared/enums/b2b";
import { isDate, isNumber, isString } from "lodash";
import { AnyObject } from "@/src/shared/types/any";
import { BE_DATE_FORMAT, BE_DATE_TIME_FORMAT, BE_TIME_FORMAT } from "@/src/shared/constants/date";
import timeEntry from "@/src/shared/utils/timeEntry";

/**
 * Format dates with dayjs.js with format from global settings
 */
export const formatDate = function (date?: Date | string | null, withTime = false): string {
  if (!date) return "";
  const jsFormat = getDateOrDateTimeFormat(withTime);
  if (withTime) {
    return dayjs(parseDate(date)).format(jsFormat);
  }
  return dayjs(date).format(jsFormat);
};

export const getNumberOfMonths = (
  startDate: string | Date | Dayjs,
  endDate: string | Date | Dayjs
): number | ErrorsEnum => {
  if (!startDate || !endDate) return ErrorsEnum.NA;

  const start = resolveBorderDate(startDate);
  const isValidStart = start.isValid();
  const end = resolveBorderDate(endDate);
  const adjustedEnd = end.add(1, "day");
  const isValidEnd = adjustedEnd.isValid();

  if (!isValidStart || !isValidEnd || start.isAfter(adjustedEnd)) return ErrorsEnum.NA;

  return end.diff(start, "month");
};

const resolveBorderDate = (date: number | string | Date | Dayjs) => {
  const dateObject = isNumber(date) || isString(date) ? new Date(date) : date;
  return isDate(dateObject) ? dayjs(date) : dayjs(date, "YYYY-MM-DD", true);
};

export const getDateOrDateTimeFormat = function (withTime: boolean): string {
  return withTime ? window.EasySettings.getGlobalDateTimeFormat.value : window.EasySettings.getGlobalDateFormat.value;
};

export const rubyDateTimeFormatToJsFormat = function (rubyFormat?: string | null): string {
  if (!rubyFormat) return "";

  const rubyJsFormatConverter: { [rubyKey: string]: string } = {
    "%b": "MMM", // Shortened month name "Jan"
    "%B": "MMMM", // Full month name "January"
    "%m": "MM", // Numbered month of the year (01..12) "01"
    "%d": "DD", // Day of the month (01..31) "15"
    "%j": "DDD", // Day of the year (001..366) "123"
    "%a": "ddd", // Shortened weekday name Mon
    "%A": "dddd", // Full weekday name "Monday"
    "%w": "d", // The numeric day of the week (0..6) "1"
    "%U": "w", // Numeric week number in the year (0..53) [starting with the first Sunday] "22"
    "%W": "W", // Numeric week number in the year (0..53) [starting with the first Monday] "22"
    "%y": "YY", // Two-digit year (00..99) "99"
    "%Y": "YYYY", // Four-digit year "2022"
    "%H": "HH", // Hour of the day (military time) (00..23) "18"
    "%I": "hh", // Hour of the day (12-hour clock) (01..12) "08"
    "%M": "mm", // Minute of the hour (0..59) "15"
    "%S": "s", // Second of the minute (0..59) "30"
    "%p": "A", // AM or PM "PM"
    "%Z": "z", // Time zone name "Central Time (US & Canada)"
  };

  let response = rubyFormat;

  Object.keys(rubyJsFormatConverter).forEach(rubyKey => {
    response = response.replaceAll(rubyKey, rubyJsFormatConverter[rubyKey]);
  });

  return response;
};

/**
 * Format date for send to BE
 */
export const formatDateForRequest = function (date: Date | string, type = "date") {
  if (!date) return "";
  switch (type) {
    case "datetime":
      return dayjs(date).format("YYYY-MM-DD HH:mm");
    case "time":
      return dayjs(date).format("HH:mm");
    default:
      return dayjs(date).format("YYYY-MM-DD");
  }
};

export const dateISOStringParseZone = function (date: string | Date | null) {
  if (!date || !dayjs(date).isValid()) return null;

  return dayjs(date).utc(true).toISOString();
};

export const getFirstDayOfWeek = function () {
  if (!window.EASY || !window.EASY.datepickerOptions) return 0;
  return window.EASY.datepickerOptions.firstDay;
};

/**
 * Get time and date as arguments
 * Usefull for timepickers where need to change a date and save time
 */
export const moveDate = (date: string | null, time: string): string => {
  if (!date) {
    return dayjs(time).format(BE_DATE_FORMAT);
  }

  const [hours, minutes] = timeEntry.arrayTime(dayjs(time).format(BE_TIME_FORMAT));
  const newDate = dayjs(date);
  return newDate.hour(hours).minute(minutes).format(BE_DATE_TIME_FORMAT);
};

export const crossBrowserISOString = function (date: string | Date | Dayjs, withTime = false): string {
  const dayjsDate = dayjs(date, getDateOrDateTimeFormat(withTime), true);
  let isoDate = dayjsDate.isValid() ? dayjsDate.toISOString() : dayjs(date).toISOString();
  if (!isoDate) {
    isoDate = dayjs(date, "YYYY-MM-DD HH:mm").toISOString();
    isoDate = isoDate?.substring(0, isoDate.length - 10);
  }
  return isoDate;
};

export const parseDatetime = function (date: string | Date | Dayjs): Date {
  const easyUtils = window.EASY?.utils;
  date = crossBrowserISOString(date, true);
  if (easyUtils?.parseDate) {
    return easyUtils.parseDate(date);
  }
  // eslint-disable-next-line no-console
  console.error("You don't have access to a function: EASY.utils.parseDate");
  return new Date(date);
};

export const parseDateOnly = function (date: string | Date | Dayjs): Date {
  if (date instanceof Date) return date;
  const dateString = typeof date === "string" ? date : String(date);

  let parsedDate = dayjs(dateString, BE_DATE_FORMAT, true);
  if (!parsedDate.isValid()) {
    const globalDateFormat = window.EasySettings?.getGlobalDateFormat?.value;
    if (globalDateFormat) {
      parsedDate = dayjs(dateString, globalDateFormat, true);
    }
  }

  if (!parsedDate.isValid()) {
    return new Date(dateString);
  }

  return parsedDate.hour(12).minute(0).second(0).millisecond(0).toDate();
};

export const parseDate = function (date: string | Date | Dayjs, options: { dateOnly?: boolean } = {}): Date {
  if (options.dateOnly) {
    return parseDateOnly(date);
  }

  const easyUtils = window.EASY?.utils;
  date = crossBrowserISOString(date, false);
  if (easyUtils?.parseDate) {
    return easyUtils.parseDate(date);
  }
  // eslint-disable-next-line no-console
  console.error("You don't have access to a function: EASY.utils.parseDate");
  return new Date(date);
};

export const recountDate = ({
  form,
  toUpdate,
  changed,
  isAdding,
}: {
  form: AnyObject;
  toUpdate: string;
  changed: string;
  isAdding: boolean;
}): void => {
  const changedValue = form[changed];
  if (!changedValue) return;
  const countedDate = isAdding
    ? dayjs(changedValue).add(form.usermonths, "months").subtract(1, "day")
    : dayjs(changedValue).subtract(form.usermonths, "months").add(1, "day");
  form[toUpdate] = dayjs(countedDate).isValid() ? dayjs(countedDate).format(BE_DATE_FORMAT) : null;
};

export const isValidDateTime = (dateString?: string | null) => {
  if (!dateString) return false;
  return dayjs(dateString).isValid();
};
